import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { toast } from '@localagent/ui/components/sonner';
import { useAuthenticateApiKey } from '../../hooks/useIntegrations';

interface ApiKeyAuthDialogProps {
  connectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ApiKeyAuthDialog({ connectionId, open, onOpenChange, onSuccess }: ApiKeyAuthDialogProps) {
  const [apiKey, setApiKey] = useState('');
  const authApiKey = useAuthenticateApiKey();

  const handleSubmit = () => {
    if (!apiKey.trim()) return;
    authApiKey.mutate(
      { connectionId, apiKey },
      {
        onSuccess: () => {
          toast.success('API key authenticated successfully');
          setApiKey('');
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (err) => {
          toast.error(`Authentication failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API Key Authentication</DialogTitle>
          <DialogDescription>Enter your API key to authenticate this connection.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="api-key">API Key</Label>
            <Input
              id="api-key"
              type="password"
              placeholder="Enter your API key"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKey(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!apiKey.trim() || authApiKey.isPending}>
            {authApiKey.isPending ? 'Authenticating...' : 'Authenticate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
